import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, MapPin, PenLine, ChevronRight } from "lucide-react";

// Worker-facing "days you haven't finished" list. Shows the logged-in worker's own assigned jobs
// due today or earlier that still aren't completed, flagging which have no notes saved yet.
export default function CatchUpBanner({ jobPath = (id) => `/jobs/${id}` }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/my/incomplete-days").then((r) => setRows(r.data?.incomplete_days || [])).catch(() => {});
  }, []);

  if (rows.length === 0) return null;
  const noNotes = rows.filter((r) => !r.has_notes).length;

  return (
    <div data-testid="catch-up-banner" className="bg-white rounded-2xl border border-amber-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 bg-amber-50 border-b border-amber-200 px-4 py-3.5">
        <span className="w-9 h-9 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5" /></span>
        <div>
          <h2 className="font-heading font-semibold text-amber-900 text-sm">
            {rows.length} day{rows.length > 1 ? "s" : ""} to finish
          </h2>
          <p className="text-xs text-amber-700">
            These weren't completed on the day{noNotes > 0 ? ` · ${noNotes} still ${noNotes > 1 ? "have" : "has"} no notes` : ""}. Tap to finish so your pay isn't held up.
          </p>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((j) => (
          <button key={j.id} data-testid={`catch-up-row-${j.id}`} onClick={() => navigate(jobPath(j.id))}
            className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-amber-50/50 transition-colors">
            <div className="shrink-0 text-center w-16">
              <span className={`block text-xs font-bold px-2 py-1 rounded-md ${j.days_overdue > 0 ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>
                {j.days_overdue > 0 ? `${j.days_overdue}d late` : "Today"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-navy truncate text-sm">{j.customer_name || "Job"} <span className="text-slate-400 font-normal">· {j.job_number}</span></p>
              <p className="text-xs text-slate-500 truncate flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" />{j.site_address || "No address"}</p>
              {!j.has_notes && (
                <span data-testid={`catch-up-no-notes-${j.id}`} className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                  <PenLine className="w-3 h-3" />No notes yet
                </span>
              )}
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}
